import { useEffect, useState } from "react";

import type { CopySettings, FollowRelationship, RemoteData, TraderProfile } from "./types";

const API_BASE = (import.meta.env.VITE_COPY_TRADING_API ?? "http://localhost:8009").replace(/\/$/, "");

async function apiFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`API ${path} → ${res.status}`);
  return res.json();
}

// The API returns avatar URLs relative to the copy-trading host; make absolute.
export function avatarFullUrl(u?: string | null): string | undefined {
  if (!u) return undefined;
  return /^https?:\/\//i.test(u) ? u : `${API_BASE}${u}`;
}

export type ProfileUpdateResult = { name: string | null; avatarUrl?: string };

/**
 * Update a trader's profile (display name and/or photo). The trader signs a
 * message proving they control `address`; the server verifies the signature
 * before persisting. `dataUrl`, when provided, is a base64 webp/png/jpeg from
 * the client-side cropper. Returns the saved name and absolute avatar URL.
 */
export async function updateProfile(
  signer: { getAddress(): Promise<string>; signMessage(msg: string): Promise<string> },
  opts: { name?: string; dataUrl?: string }
): Promise<ProfileUpdateResult> {
  const address = (await signer.getAddress()).toLowerCase();
  const timestamp = Date.now();
  const name = (opts.name ?? "").trim();
  const message =
    `MoneyX Copy Trading — update profile\n` +
    `address: ${address}\n` +
    `name: ${name}\n` +
    `timestamp: ${timestamp}`;
  const signature = await signer.signMessage(message);

  const res = await fetch(`${API_BASE}/api/copy/profile`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ address, timestamp, signature, name, image: opts.dataUrl }),
  });
  if (!res.ok) {
    let msg = `Update failed (${res.status})`;
    try { msg = (await res.json()).error || msg; } catch { /* keep default */ }
    throw new Error(msg);
  }
  const body = (await res.json()) as { name: string | null; avatarUrl?: string };
  return { name: body.name ?? null, avatarUrl: avatarFullUrl(body.avatarUrl) };
}

/** Read a trader's stored profile (name + avatar), if any. */
export async function fetchProfile(address: string): Promise<{ name: string | null; avatarUrl?: string }> {
  try {
    const r = await apiFetch<{ name: string | null; avatarUrl?: string | null }>(`/api/copy/profile/${address.toLowerCase()}`);
    return { name: r.name ?? null, avatarUrl: avatarFullUrl(r.avatarUrl) };
  } catch {
    return { name: null };
  }
}

// ── Leaderboard ───────────────────────────────────────────────────────────────

// Shape returned by GET /api/copy/leaderboard
type LeaderboardApiResponse = {
  total: number;
  page: number;
  pageSize: number;
  traders: Array<{
    address: string;
    copyFeeRate: string;
    maxFollowers: string;
    followerCount: string;
    totalVolume: string;
    realisedPnl: string;
    totalTrades: number;
    winRate?: number;
    avgLeverage?: number;
    avatarUrl?: string | null;
    displayName?: string | null;
  }>;
};

// The API may serialise 1e30-scaled USD values as floats in scientific notation
// (e.g. "3.99e+31"), which BigInt() cannot parse and would throw on. Parse those
// safely by rounding through Number; plain integer strings stay exact.
function toBigIntSafe(v: string | number | null | undefined): bigint {
  if (v === null || v === undefined || v === "") return 0n;
  const s = String(v).trim();
  if (/^-?\d+$/.test(s)) return BigInt(s);
  const n = Number(s);
  return Number.isFinite(n) ? BigInt(Math.trunc(n)) : 0n;
}

function normaliseTrader(r: LeaderboardApiResponse["traders"][number]): TraderProfile {
  const pnl = toBigIntSafe(r.realisedPnl);
  return {
    address:      r.address,
    isRegistered: true,
    copyFeeRate:  Number(r.copyFeeRate),
    maxFollowers: Number(r.maxFollowers),
    followerCount: Number(r.followerCount),
    pnl30d:       pnl,
    // pnlPercent30d needs base collateral — approximate from PnL / volume
    pnlPercent30d: Number(r.totalVolume) > 0
      ? (Number(r.realisedPnl) / Number(r.totalVolume)) * 100
      : undefined,
    totalTrades:  r.totalTrades,
    totalVolume:  toBigIntSafe(r.totalVolume),
    winRate:      typeof r.winRate === "number" ? r.winRate : undefined,
    avgLeverage:  typeof r.avgLeverage === "number" ? r.avgLeverage : undefined,
    avatarUrl:    avatarFullUrl(r.avatarUrl),
    displayName:  r.displayName ?? undefined,
  };
}

export function useCopyTradingLeaderboard(sort = "followers", limit = 100) {
  const [state, setState] = useState<RemoteData<TraderProfile[]>>({ status: "loading" });

  useEffect(() => {
    let alive = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let attempt = 0;

    // Show the loading skeleton only on the very first load; background refreshes
    // and retries keep the last-good data on screen instead of flashing.
    setState((prev) => (prev.status === "success" ? prev : { status: "loading" }));

    const load = () => {
      apiFetch<LeaderboardApiResponse>(`/api/copy/leaderboard?sort=${sort}&pageSize=${limit}`)
        .then((r) => {
          if (!alive) return;
          attempt = 0;
          setState({ status: "success", data: r.traders.map(normaliseTrader) });
        })
        .catch((e) => {
          if (!alive) return;
          // Transient backend hiccup (service restart, cold cache): retry with
          // backoff instead of latching a permanent error the user must refresh past.
          attempt += 1;
          setState((prev) =>
            prev.status === "success" ? prev : { status: "error", error: e.message }
          );
          if (attempt <= 5) {
            const delay = Math.min(1000 * 2 ** (attempt - 1), 15000);
            retryTimer = setTimeout(load, delay);
          }
        });
    };

    load();
    // Keep the board fresh and self-heal from any earlier error state.
    pollTimer = setInterval(load, 20000);

    return () => {
      alive = false;
      if (retryTimer) clearTimeout(retryTimer);
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [sort, limit]);

  return state;
}

// ── Following ─────────────────────────────────────────────────────────────────

// Shape returned by GET /api/copy/following/:follower (flat array)
type FollowingApiItem = {
  trader: string;
  isActive: boolean;
  isPaused: boolean;
  collateralPerTrade: string;
  maxTotalAllocation: string;
  currentAllocation: string;
  copyFeeRate: string;
  maxFollowers: string;
  followerCount: string;
};

export function useCopyTradingFollowing(account: string | undefined): RemoteData<FollowRelationship[]> {
  const [state, setState] = useState<RemoteData<FollowRelationship[]>>({ status: "loading" });

  useEffect(() => {
    if (!account) {
      setState({ status: "success", data: [] });
      return;
    }
    apiFetch<FollowingApiItem[]>(`/api/copy/following/${account}`)
      .then((items) => {
        const data: FollowRelationship[] = items.map((item) => ({
          trader: {
            address:      item.trader,
            isRegistered: true,
            copyFeeRate:  Number(item.copyFeeRate),
            maxFollowers: Number(item.maxFollowers),
            followerCount: Number(item.followerCount),
          },
          settings: {
            isActive:           item.isActive,
            isPaused:           item.isPaused,
            collateralPerTrade: BigInt(item.collateralPerTrade),
            maxTotalAllocation: BigInt(item.maxTotalAllocation),
            currentAllocation:  BigInt(item.currentAllocation),
          },
        }));
        setState({ status: "success", data });
      })
      .catch((e) => setState({ status: "error", error: e.message }));
  }, [account]);

  return state;
}

// ── Trader detail ─────────────────────────────────────────────────────────────

// Shape returned by GET /api/copy/trader/:address
type TraderDetailApiResponse = {
  address: string;
  avatarUrl?: string | null;
  displayName?: string | null;
  isRegistered: boolean;
  copyFeeRate: string;
  maxFollowers: string;
  followerCount: string;
  followers: string[];
  totalVolume: string;
  realisedPnl: string;
  totalTrades: number;
  copyHistory: Array<{
    action: "increase" | "decrease";
    follower: string;
    collateralToken: string;
    indexToken: string;
    sizeDelta: string;
    collateralDelta: string;
    isLong: boolean;
    executionPrice: string;
    copyFee: string;
    block: number;
  }>;
};

export type TraderDetailData = {
  address: string;
  avatarUrl?: string;
  displayName?: string | null;
  profile: {
    isRegistered: boolean;
    copyFeeRate: number;
    maxFollowers: number;
    followerCount: number;
  };
  followers: string[];
  stats: {
    totalTrades: number;
    totalVolume: bigint;
    realisedPnl: bigint;
  };
  copyHistory: TraderDetailApiResponse["copyHistory"];
};

export function useTraderDetail(address: string | undefined): RemoteData<TraderDetailData> {
  const [state, setState] = useState<RemoteData<TraderDetailData>>({ status: "loading" });

  useEffect(() => {
    if (!address) return;
    apiFetch<TraderDetailApiResponse>(`/api/copy/trader/${address}`)
      .then((r) => {
        setState({
          status: "success",
          data: {
            address: r.address,
            avatarUrl: avatarFullUrl(r.avatarUrl),
            displayName: r.displayName ?? undefined,
            profile: {
              isRegistered: r.isRegistered,
              copyFeeRate:  Number(r.copyFeeRate),
              maxFollowers: Number(r.maxFollowers),
              followerCount: Number(r.followerCount),
            },
            followers: r.followers,
            stats: {
              totalTrades:  r.totalTrades,
              totalVolume:  toBigIntSafe(r.totalVolume),
              realisedPnl:  toBigIntSafe(r.realisedPnl),
            },
            copyHistory: r.copyHistory,
          },
        });
      })
      .catch((e) => setState({ status: "error", error: e.message }));
  }, [address]);

  return state;
}

// ── Portfolio ─────────────────────────────────────────────────────────────────

// Shape returned by GET /api/copy/portfolio/:follower
type PortfolioApiResponse = {
  follower: string;
  totalAllocation: string;
  positionCount: number;
  positions: Array<{
    trader: string;
    isActive: boolean;
    isPaused: boolean;
    collateralPerTrade: string;
    maxTotalAllocation: string;
    currentAllocation: string;
  }>;
  traderProfile: {
    copyFeeRate: string;
    maxFollowers: string;
    followerCount: string;
  } | null;
};

export type PortfolioData = {
  follower: string;
  totalAllocation: bigint;
  positionCount: number;
  positions: Array<{
    trader: string;
    settings: CopySettings;
  }>;
  traderProfile: {
    copyFeeRate: number;
    maxFollowers: number;
    followerCount: number;
  } | null;
};

export function useCopyPortfolio(account: string | undefined): RemoteData<PortfolioData> {
  const [state, setState] = useState<RemoteData<PortfolioData>>({ status: "loading" });

  useEffect(() => {
    if (!account) {
      setState({
        status: "success",
        data: { follower: "", totalAllocation: 0n, positionCount: 0, positions: [], traderProfile: null },
      });
      return;
    }
    apiFetch<PortfolioApiResponse>(`/api/copy/portfolio/${account}`)
      .then((r) => {
        setState({
          status: "success",
          data: {
            follower:        r.follower,
            totalAllocation: BigInt(r.totalAllocation || "0"),
            positionCount:   r.positionCount,
            positions: r.positions.map((p) => ({
              trader: p.trader,
              settings: {
                isActive:           p.isActive,
                isPaused:           p.isPaused,
                collateralPerTrade: BigInt(p.collateralPerTrade),
                maxTotalAllocation: BigInt(p.maxTotalAllocation),
                currentAllocation:  BigInt(p.currentAllocation),
              },
            })),
            traderProfile: r.traderProfile
              ? {
                  copyFeeRate:  Number(r.traderProfile.copyFeeRate),
                  maxFollowers: Number(r.traderProfile.maxFollowers),
                  followerCount: Number(r.traderProfile.followerCount),
                }
              : null,
          },
        });
      })
      .catch((e) => setState({ status: "error", error: e.message }));
  }, [account]);

  return state;
}
